// src/hooks/useVacaciones.ts

import { useState, useCallback } from "react";
import api from "../api/axios";

/* =====================================================================
 * Tipos
 * ===================================================================*/
export type IsoDate = string; // «YYYY-MM-DD»
export type Tramo = "manana" | "tarde" | "completo";

export interface VacacionData {
  fecha: IsoDate;
  tramo: Tramo;
  motivo?: string;
}

export interface ResumenVacaciones {
  curso: string; // "2024-2025"
  asignados: number; // 30 + saldo arrastrado
  disfrutados: number; // días aprobados en ese curso (0.5 por medio día)
  saldo: number; // asignados - disfrutados
}

export interface UseVacaciones {
  vacData: VacacionData[]; // días de vacaciones aprobados
  resumen: ResumenVacaciones[]; // saldo por curso
  loading: boolean; // spinner principal (vacData)
  loadingResumen: boolean; // spinner resumen
  refresh: () => Promise<void>; // vuelve a leer vacData
  refreshResumen: () => Promise<void>; // vuelve a leer resumen
  sync: (vacs: VacacionData[]) => Promise<void>; // sobrescribe en backend
}

/* =====================================================================
 * Hook principal
 * ===================================================================*/
export function useVacaciones(idProfesor: number): UseVacaciones {
  const [vacData, setVacData] = useState<VacacionData[]>([]);
  const [resumen, setResumen] = useState<ResumenVacaciones[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingResumen, setLoadingResumen] = useState(false);

  /* -------- READ aprobadas -------- */
  const refresh = useCallback(async () => {
    if (!idProfesor) return;
    setLoading(true);
    try {
      const { data } = await api.get<VacacionData[]>("/vacaciones", {
        params: { profesor: idProfesor },
      });
      setVacData(
        data.map((v) => ({
          fecha: v.fecha.slice(0, 10) as IsoDate,
          tramo: v.tramo,
          motivo: v.motivo,
        }))
      );
    } finally {
      setLoading(false);
    }
  }, [idProfesor]);

  /* -------- READ resumen -------- */
  const refreshResumen = useCallback(async () => {
    if (!idProfesor) return;
    setLoadingResumen(true);
    try {
      const { data } = await api.get<ResumenVacaciones[]>(`/vacaciones/resumen/${idProfesor}`);
      setResumen(data);
    } finally {
      setLoadingResumen(false);
    }
  }, [idProfesor]);

  /* -------- SYNC vacData -------- */
  const sync = useCallback(
    (vacs: VacacionData[]) =>
      api.put(`/vacaciones/profesor/${idProfesor}/sync`, vacs).then(async () => {
        await refresh();
        await refreshResumen();
      }),
    [idProfesor, refresh, refreshResumen]
  );

  return {
    vacData,
    resumen,
    loading,
    loadingResumen,
    refresh,
    refreshResumen,
    sync,
  };
}
